<template>
  <Transition name="player-bar">
    <div
      v-if="player.currentItem.value"
      class="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-xl flex-shrink-0 px-4 pt-2"
      style="padding-bottom: calc(0.5rem + var(--safe-bottom))">
      <div class="max-w-7xl mx-auto flex items-center gap-4">
        <div
          class="w-10 h-10 rounded flex-shrink-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
          <img
            v-if="
              player.currentItem.value.thumb ||
              player.currentItem.value.thumbnailPath ||
              player.currentItem.value.mediaType === 'video'
            "
            :src="player.currentItem.value.thumb || thumbnailUrl(player.currentItem.value.id)"
            alt=""
            class="w-full h-full object-cover" />
          <span v-else class="material-symbols-outlined text-gray-400 text-lg">
            {{ player.currentItem.value.mediaType === 'audio' ? 'music_note' : 'movie' }}
          </span>
        </div>

        <div class="min-w-0 w-36 flex-shrink-0 hidden sm:block">
          <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ player.currentItem.value.title }}</p>
          <p class="text-xs text-gray-500 truncate">{{ player.currentItem.value.artist || '' }}</p>
          <p v-if="queueCount > 1" class="text-[10px] text-gray-400 tabular-nums">
            {{ tt(`Track ${queueIndex + 1} of ${queueCount}`, `Skladba ${queueIndex + 1} od ${queueCount}`) }}
          </p>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            @click="player.skipPrevious()"
            :disabled="queueCount === 0"
            class="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            :title="tt('Previous track (restart if at the first)', 'Prejšnja skladba (ob prvi se ponovi)')">
            <span class="material-symbols-outlined text-xl">skip_previous</span>
          </button>
          <button
            @click="player.seekRelative(-10)"
            class="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 transition-colors"
            :title="tt('Back 10 seconds', 'Nazaj 10 sekund')">
            <span class="material-symbols-outlined text-xl">replay_10</span>
          </button>
          <button
            @click="player.toggleBrowserPlay()"
            class="w-9 h-9 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            :title="player.browserPlaying.value ? tt('Pause', 'Premor') : tt('Play', 'Predvajaj')">
            <span class="material-symbols-outlined text-xl">{{
              player.browserPlaying.value ? 'pause' : 'play_arrow'
            }}</span>
          </button>
          <button
            @click="player.seekRelative(10)"
            class="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 transition-colors"
            :title="tt('Forward 10 seconds', 'Naprej 10 sekund')">
            <span class="material-symbols-outlined text-xl">forward_10</span>
          </button>
          <button
            @click="player.skipNext()"
            :disabled="queueCount === 0 || queueIndex >= queueCount - 1"
            class="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            :title="tt('Next track', 'Naslednja skladba')">
            <span class="material-symbols-outlined text-xl">skip_next</span>
          </button>
        </div>

        <div class="flex items-center gap-2 flex-1 min-w-0">
          <span class="text-xs text-gray-500 w-10 text-right flex-shrink-0">{{
            player.formatPlayerTime(player.browserCurrentTime.value)
          }}</span>
          <input
            type="range"
            min="0"
            :max="player.browserDuration.value || 100"
            step="0.1"
            :value="player.browserCurrentTime.value"
            @input="player.seekTo(($event.target as HTMLInputElement).valueAsNumber)"
            class="flex-1 h-1.5 appearance-none rounded-full bg-gray-300 dark:bg-gray-600 accent-blue-500 cursor-pointer" />
          <span class="text-xs text-gray-500 w-10 flex-shrink-0">{{
            player.formatPlayerTime(player.browserDuration.value)
          }}</span>
        </div>

        <div class="items-center gap-1.5 flex-shrink-0 hidden md:flex">
          <button
            @click="
              player.browserMuted.value = !player.browserMuted.value;
              player.applyVolume();
            "
            class="text-gray-500 dark:text-gray-400 hover:text-blue-500 transition-colors"
            :title="player.browserMuted.value ? tt('Unmute', 'Vklopi zvok') : tt('Mute', 'Izklopi zvok')">
            <span class="material-symbols-outlined text-xl">
              {{
                player.browserMuted.value
                  ? 'volume_off'
                  : player.browserVolume.value > 0.5
                    ? 'volume_up'
                    : 'volume_down'
              }}
            </span>
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            :value="player.browserMuted.value ? 0 : player.browserVolume.value"
            @input="player.onVolumeInput(($event.target as HTMLInputElement).valueAsNumber)"
            class="w-20 h-1.5 appearance-none rounded-full bg-gray-300 dark:bg-gray-600 accent-blue-500 cursor-pointer" />
        </div>

        <button
          @click="ui.openPlaylistDrawer()"
          class="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
          :title="tt('Playlists', 'Predvajalni seznami')">
          <span class="material-symbols-outlined text-xl">queue_music</span>
        </button>

        <button
          v-if="player.currentItem.value.mediaType === 'video'"
          @click="player.showVideoPopup.value = !player.showVideoPopup.value"
          class="p-1 transition-colors flex-shrink-0"
          :class="player.showVideoPopup.value ? 'text-blue-500' : 'text-gray-500 hover:text-blue-500'"
          :title="player.showVideoPopup.value ? tt('Collapse video', 'Strni video') : tt('Show video', 'Pokaži video')">
          <span class="material-symbols-outlined text-xl">picture_in_picture_alt</span>
        </button>

        <button
          @click="player.stopBrowser()"
          class="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
          :title="tt('Stop', 'Ustavi')">
          <span class="material-symbols-outlined text-xl">stop</span>
        </button>

        <button
          @click="player.closeBrowserPlayer()"
          class="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
          :title="tt('Close', 'Zapri')">
          <span class="material-symbols-outlined text-xl">close</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBrowserPlayer } from '@/composables/useBrowserPlayer';
import { getMediaThumbnailUrl } from '@/services/mediaLibraryUrls';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { tt } from '../../utils/consoleText';

const player = useBrowserPlayer();
const ui = useUIStore();
const authStore = useAuthStore();

const queueCount = computed(() => player.playerQueue.value.length);
const queueIndex = computed(() => player.playerQueueIndex.value);

function thumbnailUrl(itemId: string): string {
  return (
    getMediaThumbnailUrl(
      itemId,
      authStore.token ?? localStorage.getItem('accessToken') ?? localStorage.getItem('token') ?? '',
    ) ?? ''
  );
}
</script>

<style scoped>
.player-bar-enter-active,
.player-bar-leave-active {
  transition: transform 0.25s ease;
}
.player-bar-enter-from,
.player-bar-leave-to {
  transform: translateY(100%);
}
</style>
